import dayjs from "$routes/_lib/dayjs";
import { Box, makeStyles, Typography } from "@material-ui/core";
import classnames from "classnames";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { INPATIENT } from "../formConstants";
import InsuranceHeader from "./InsuranceHeader";

const DATE_FORMAT = "DD/MM/YYYY";

const useStyles = makeStyles((theme) => ({
  insurer: {
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  patient: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  names: {
    fontSize: 16,
  },
  container: {
    display: "flex",
    justifyContent: "space-between",
  },
  facility: {
    marginLeft: theme.spacing(2),
  },
  label: {
    fontSize: 12,
    marginTop: theme.spacing(1),
  },
  listItem: {
    display: "flex",
    fontSize: 14,
  },
  row: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between",
  },
  column: {
    display: "flex",
    flexDirection: "column",
    width: "33.333%",
  },
}));

function getInpatientDates(inpatientDetails = {}) {
  const { admission, discharge } = inpatientDetails;

  if (admission && discharge) {
    return `${dayjs(admission).format(DATE_FORMAT)}-${dayjs(discharge).format(
      DATE_FORMAT,
    )}`;
  }

  return "-";
}

const InsuranceDetails = ({ insurer, invoice }) => {
  const { patient, createdBy } = invoice;
  const insuranceDetails = insurer?.insuranceDetails;
  const {
    person: {
      name: { firstname, lastname, middlename },
    },
  } = patient;
  const { person } = createdBy;

  const capitalize = (s) => s && s[0].toUpperCase() + s.slice(1).toLowerCase();

  const classes = useStyles();
  const dependency =
    insuranceDetails?.dependent || insuranceDetails?.dependency || "";
  return (
    <Box className={classes.insurer}>
      <InsuranceHeader insurer={insurer} invoice={invoice} />
      <Box className={classes.container}>
        <Box className={classes.patient}>
          <Typography className={classes.names}>
            <strong>{`${firstname} ${middlename || ""} ${lastname}`}</strong>
          </Typography>
          <Box>
            {invoice.inpatientDetails && (
              <Typography className={classes.label}>
                <strong>
                  <FormattedMessage
                    id="printableInvoice.patient.inpatientNumber"
                    defaultMessage="Inpatient Number:"
                  />
                </strong>
                {` ${invoice.inpatientDetails?.inpatientNumber}`}
              </Typography>
            )}
          </Box>
          <div className={classes.row}>
            <div className={classes.column}>
              {dependency && (
                <div className={classes.cell}>
                  <Typography className={classes.label}>
                    <strong>
                      <FormattedMessage
                        id="printableInvoice.patient.dependency"
                        defaultMessage="Dependant:"
                      />
                    </strong>
                    {` ${capitalize(dependency.replace(/dependent/i, ""))}`}
                  </Typography>
                </div>
              )}
              {insuranceDetails?.membershipNumber ||
                (insuranceDetails?.insuranceNumber && (
                  <div className={classes.cell}>
                    <Typography className={classes.label}>
                      <strong>
                        <FormattedMessage
                          id="printableInvoice.patient.membershipNumber"
                          defaultMessage="Membership No:"
                        />
                      </strong>
                      {` ${
                        insuranceDetails?.membershipNumber ||
                        insuranceDetails?.insuranceNumber
                      }`}
                    </Typography>
                  </div>
                ))}
            </div>
            <div className={classes.column}>
              <Box>
                <div className={classnames(classes.cell, classes.textRight)}>
                  <Typography className={classes.label}>
                    <strong>
                      <FormattedMessage
                        id="printableInvoice.insurer.visitType"
                        defaultMessage="Visit type:"
                      />
                    </strong>
                    {invoice.patientType === INPATIENT ? (
                      <>
                        {" "}
                        <FormattedMessage
                          id="printableInvoice.insurer.visitType.inpatient"
                          defaultMessage="Inpatient"
                        />
                      </>
                    ) : (
                      <>
                        {" "}
                        <FormattedMessage
                          id="printableInvoice.insurer.visitType.outpatient"
                          defaultMessage="General"
                        />
                      </>
                    )}
                  </Typography>
                </div>
              </Box>
              <Box>
                <div className={classnames(classes.cell, classes.textRight)}>
                  <Typography className={classes.label}>
                    <strong>
                      <FormattedMessage
                        id="printableInvoice.insurer.visitDate"
                        defaultMessage="Visit date:"
                      />
                    </strong>
                    {invoice.patientType === INPATIENT
                      ? ` ${getInpatientDates(invoice.inpatientDetails)}`
                      : ` ${dayjs(new Date(invoice.created)).format(
                          DATE_FORMAT,
                        )}`}
                  </Typography>
                </div>
              </Box>
            </div>
            <div className={classes.column}>
              <Box>
                <div className={classnames(classes.cell, classes.textRight)}>
                  <Typography className={classes.label}>
                    <strong>
                      <FormattedMessage
                        id="printableInvoice.insurer.invoicedOn"
                        defaultMessage="Invoiced on:"
                      />
                    </strong>
                    {` ${dayjs(new Date(invoice.created)).format(DATE_FORMAT)}`}
                  </Typography>
                </div>
              </Box>
              <Box>
                <div className={classnames(classes.cell, classes.textRight)}>
                  <Typography className={classes.label}>
                    <strong>
                      <FormattedMessage
                        id="printableInvoice.insurer.preparedBy"
                        defaultMessage="Prepared by:"
                      />
                    </strong>
                    {` ${person?.firstname} ${person?.lastname}`}
                  </Typography>
                </div>
              </Box>
            </div>
          </div>
        </Box>
      </Box>
    </Box>
  );
};

InsuranceDetails.propTypes = {
  insurer: PropTypes.shape({
    insuranceDetails: PropTypes.shape({
      membershipNumber: PropTypes.string,
    }),
  }).isRequired,
  invoice: PropTypes.shape({
    patient: PropTypes.shape({
      insurance: PropTypes.shape({
        insuranceDetails: PropTypes.shape({
          dependency: PropTypes.string,
          insuranceNumber: PropTypes.string,
        }),
      }),
      person: PropTypes.shape({
        name: PropTypes.shape({
          firstname: PropTypes.string,
          lastname: PropTypes.string,
        }),
      }),
    }),
    facility: PropTypes.shape({
      name: PropTypes.string.isRequired,
      addresses: PropTypes.arrayOf(
        PropTypes.shape({
          street: PropTypes.string,
          ward: PropTypes.string,
          subCounty: PropTypes.string,
          state: PropTypes.string,
        }),
      ),
    }).isRequired,
  }).isRequired,
};

export default InsuranceDetails;
